import React from 'react';

import { formatDate } from '../../utils/format-date';
import { formatCurrency } from '../../utils/format-currency';
import type { ReceiptData } from '../../utils/receipt';

interface ReceiptTicketProps {
  data: ReceiptData;
}

export const ReceiptTicket = React.forwardRef<HTMLDivElement, ReceiptTicketProps>(
  ({ data }, ref) => {
    // Helper to ensure we don't show NaN
    const safeCurrency = (amount?: number | null) => {
      if (amount === undefined || amount === null || !Number.isFinite(amount)) {
        return formatCurrency(0);
      }
      return formatCurrency(amount);
    };

    return (
      <div
        ref={ref}
        data-testid="receipt-ticket"
        className="print:block"
        style={{
          width: '80mm',
          fontFamily: '"Courier New", Courier, monospace', // Classic receipt font
          fontSize: '12px',
          lineHeight: '1.4',
          color: '#000',
          padding: '10px',
          background: 'white',
        }}
      >
        {/* HEADER */}
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
          <h1 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 5px 0', textTransform: 'uppercase' }}>
            DUFI WAITER
          </h1>
          <p style={{ margin: 0, fontSize: '10px' }}>Sistema de Gestión</p>
          <div style={{ margin: '10px 0', borderBottom: '1px dashed #000' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
            <span>Fecha:</span>
            <span>{formatDate(data.paidAt, "dd/MM/yy HH:mm")}</span>
          </div>
          {data.orderNumber && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
              <span>Orden #:</span>
              <span style={{ fontWeight: 'bold' }}>{data.orderNumber}</span>
            </div>
          )}
          {data.tableLabel && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
              <span>Mesa/Tipo:</span>
              <span>{data.tableLabel}</span>
            </div>
          )}
          {data.customerName && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
              <span>Cliente:</span>
              <span style={{ maxWidth: '60%', textAlign: 'right' }}>{data.customerName}</span>
            </div>
          )}
        </div>

        {/* ITEMS */}
        <div style={{ borderBottom: '1px dashed #000', paddingBottom: '10px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', fontWeight: 'bold', marginBottom: '5px', borderBottom: '1px solid #000', paddingBottom: '2px' }}>
            <span style={{ width: '10%' }}>Cnt</span>
            <span style={{ width: '60%' }}>Desc</span>
            <span style={{ width: '30%', textAlign: 'right' }}>Total</span>
          </div>

          {data.items.map((item, index) => (
            <div key={`${item.name}-${index}`} style={{ marginBottom: '6px' }}>
              <div style={{ display: 'flex' }}>
                <span style={{ width: '10%', verticalAlign: 'top' }}>{item.quantity}</span>
                <span style={{ width: '60%', verticalAlign: 'top', paddingRight: '5px' }}>
                  {item.name}
                </span>
                <span style={{ width: '30%', textAlign: 'right', verticalAlign: 'top' }}>
                  {safeCurrency(item.total)}
                </span>
              </div>
              {item.notes && (
                <div style={{ fontSize: '10px', fontStyle: 'italic', margin: '2px 0 0 10%' }}>
                  ({item.notes})
                </div>
              )}
            </div>
          ))}
        </div>

        {/* TOTALS */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '2px' }}>
            <span>Subtotal:</span>
            <span>{safeCurrency(data.subtotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '16px', fontWeight: 'bold', marginTop: '5px', borderTop: '1px solid #000', paddingTop: '5px' }}>
            <span>TOTAL:</span>
            <span>{safeCurrency(data.total)}</span>
          </div>
        </div>

        {/* PAYMENT INFO */}
        <div style={{ borderTop: '1px dashed #000', paddingTop: '10px', marginBottom: '20px' }}>
          {typeof data.received === 'number' && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Efectivo/Recibido:</span>
              <span>{safeCurrency(data.received)}</span>
            </div>
          )}
          {typeof data.change === 'number' && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Cambio:</span>
              <span>{safeCurrency(data.change)}</span>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ textAlign: 'center', fontSize: '10px' }}>
          <p style={{ margin: '5px 0' }}>¡Gracias por su compra!</p>
          <p style={{ margin: 0 }}>Vuelva pronto</p>
        </div>
      </div>
    );
  },
);

ReceiptTicket.displayName = 'ReceiptTicket';

export default ReceiptTicket;
